import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, TrendingUp } from 'lucide-react';

export const blogPosts = [
  {
    slug: 'react-fiber-explained',
    title: 'React Fiber Architecture: Why Your App Re-renders',
    excerpt: 'Deep dive into React\'s reconciliation engine — how Fiber enables concurrent rendering, what the work loop looks like, and how to use this knowledge to write faster components.',
    category: 'React',
    readTime: '8 min',
    date: 'Mar 18, 2026',
    featured: true,
    tags: ['React', 'Performance', 'Internals'],
    color: '#00e5c8',
  },
  {
    slug: 'typescript-discriminated-unions',
    title: 'Discriminated Unions: The TypeScript Pattern You\'re Missing',
    excerpt: 'Stop writing `if (type === "x")` checks everywhere. Learn how discriminated unions eliminate runtime errors and make your state machines bulletproof.',
    category: 'TypeScript',
    readTime: '6 min',
    date: 'Mar 12, 2026',
    featured: true,
    tags: ['TypeScript', 'Patterns', 'Type Safety'],
    color: '#8b5cf6',
  },
  {
    slug: 'building-real-time-crypto-dashboard',
    title: 'Building a Real-time Crypto Dashboard: Lessons from Pump Market Cap',
    excerpt: 'How I handled high-frequency WebSocket streams, IndexedDB caching, and TradingView integration without killing the main thread.',
    category: 'Projects',
    readTime: '10 min',
    date: 'Mar 5, 2026',
    featured: false,
    tags: ['Next.js', 'WebSockets', 'Zustand', 'Performance'],
    color: '#f59e0b',
  },
  {
    slug: 'golang-vs-nodejs-backend',
    title: 'Go vs Node.js for Backend APIs: A Senior Dev\'s Honest Take',
    excerpt: 'After shipping production APIs in both languages — here\'s what I actually learned about concurrency, developer experience, and when to choose which.',
    category: 'Backend',
    readTime: '7 min',
    date: 'Feb 26, 2026',
    featured: false,
    tags: ['Golang', 'Node.js', 'Backend', 'Architecture'],
    color: '#10b981',
  },
  {
    slug: 'usememo-when-not-to-use',
    title: 'Stop Overusing useMemo: When Memoization Hurts Performance',
    excerpt: 'useMemo is not free. I profiled 3 production apps and found that premature memoization was actually slowing things down. Here\'s the data.',
    category: 'React',
    readTime: '5 min',
    date: 'Feb 18, 2026',
    featured: false,
    tags: ['React', 'Performance', 'Hooks'],
    color: '#00e5c8',
  },
  {
    slug: 'solidity-smart-contract-patterns',
    title: 'Smart Contract Patterns That Saved Us $200k in Gas Fees',
    excerpt: 'Real patterns from building Syndr\'s on-chain registry. Packed storage, events over storage, proxy patterns — with actual gas measurements.',
    category: 'Web3',
    readTime: '9 min',
    date: 'Feb 10, 2026',
    featured: false,
    tags: ['Solidity', 'Web3', 'Gas Optimization'],
    color: '#f43f5e',
  },
];

const categories = ['All', 'React', 'TypeScript', 'Backend', 'Projects', 'Web3'];

const categoryColors: Record<string, string> = {
  React: '#00e5c8',
  TypeScript: '#8b5cf6',
  Backend: '#10b981',
  Projects: '#f59e0b',
  Web3: '#f43f5e',
  All: '#e8edf3',
};

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter(p => p.category === activeCategory);

  const featured = blogPosts.filter(p => p.featured);

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh' }}>
      {/* Header */}
      <section style={{ padding: '72px 24px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: -80, right: '10%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="badge" style={{ marginBottom: 20 }}>
            <TrendingUp size={11} /> Writing & Insights
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)',
            fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 16,
          }}>
            Thoughts on building<br />
            <span style={{ color: 'var(--cyan)' }}>great software</span>
          </h1>
          <p style={{ color: 'var(--text2)', fontSize: 18, maxWidth: 520 }}>
            Deep dives into React internals, TypeScript patterns, backend architecture, and lessons learned from shipping production apps.
          </p>
        </div>
      </section>

      {/* Featured Posts */}
      <section style={{ padding: '0 24px 64px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24 }}>Featured</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, marginBottom: 64 }}>
            {featured.map(post => (
              <Link to={`/blog/${post.slug}`} key={post.slug} style={{
                display: 'block',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: 32,
                transition: 'all 0.25s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseOver={e => {
                (e.currentTarget as HTMLElement).style.border = `1px solid ${post.color}40`;
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                (e.currentTarget as HTMLElement).style.boxShadow = `0 20px 60px ${post.color}15`;
              }}
              onMouseOut={e => {
                (e.currentTarget as HTMLElement).style.border = '1px solid var(--border)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                  background: `linear-gradient(90deg, ${post.color}, transparent)`,
                }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: post.color, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {post.category}
                  </span>
                  <span style={{ color: 'var(--text3)', fontSize: 12 }}>·</span>
                  <span style={{ color: 'var(--text3)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={11} /> {post.readTime}
                  </span>
                </div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, lineHeight: 1.25, marginBottom: 12, letterSpacing: '-0.01em' }}>
                  {post.title}
                </h2>
                <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
                  {post.excerpt}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>{post.date}</span>
                  <span style={{ color: post.color, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    Read <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Category Filter */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32 }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)} style={{
                padding: '7px 16px', borderRadius: 100, fontSize: 13, fontWeight: 500,
                border: activeCategory === cat ? `1px solid ${categoryColors[cat]}60` : '1px solid var(--border)',
                background: activeCategory === cat ? `${categoryColors[cat]}15` : 'transparent',
                color: activeCategory === cat ? categoryColors[cat] : 'var(--text2)',
                cursor: 'pointer', transition: 'all 0.2s',
              }}>
                {cat}
              </button>
            ))}
          </div>

          {/* All Posts Grid */}
          <div style={{ display: 'grid', gap: 1, border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {filtered.map((post, i) => (
              <Link to={`/blog/${post.slug}`} key={post.slug} style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center',
                gap: 24,
                padding: '24px 28px',
                background: 'var(--bg2)',
                borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                transition: 'background 0.2s',
              }}
              onMouseOver={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg3)'}
              onMouseOut={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg2)'}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 10, color: post.color,
                      fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}>{post.category}</span>
                    <span style={{ color: 'var(--text3)', fontSize: 12 }}>·</span>
                    <span style={{ color: 'var(--text3)', fontSize: 12 }}><Clock size={10} style={{ display: 'inline', marginRight: 3 }} />{post.readTime}</span>
                    <span style={{ color: 'var(--text3)', fontSize: 12 }}>·</span>
                    <span style={{ color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>{post.date}</span>
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.01em' }}>
                    {post.title}
                  </h3>
                  <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.6 }}>{post.excerpt}</p>
                  <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                    {post.tags.map(t => <span key={t} className="tag">{t}</span>)}
                  </div>
                </div>
                <ArrowRight size={18} style={{ color: 'var(--text3)', flexShrink: 0 }} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
